import React, {createContext, useContext, useState, useEffect, useCallback, useRef} from "react";
import {
  TokenBalance,
  TokenPricing,
  PaymentHistory,
  PaymentError,
  PurchaseRequest,
  PurchaseResult,
  PaginationParams
} from "../types/payment";
import {paymentService} from "../utils/PaymentService";
import {useUser} from "./AuthContext";

interface PaymentContextInterface {
  // State
  tokenBalance: TokenBalance | null;
  tokenPricing: TokenPricing | null;
  paymentHistory: PaymentHistory | null;
  isLoading: boolean;
  error: PaymentError | null;

  // Actions
  purchaseTokens: (request: PurchaseRequest) => Promise<PurchaseResult>;
  getTokenBalance: (force?: boolean) => Promise<void>;
  getTokenPricing: () => Promise<void>;
  getPaymentHistory: (pagination?: PaginationParams) => Promise<void>;
  retryFailedOperation: (operationId: string) => Promise<void>;
  clearError: () => void;
  refreshData: () => Promise<void>;
}

const DEFAULT_PAGINATION: PaginationParams = {
  limit: 20,
  offset: 0
};

const DEFAULT_TOKEN_BALANCE: TokenBalance = {
  tokens: 0,
  freeUsageRemaining: 0,
  nextResetDate: "",
  lifetimeSpent: 0
};

export const PaymentContext = createContext<PaymentContextInterface>({
  // State
  tokenBalance: null,
  tokenPricing: null,
  paymentHistory: null,
  isLoading: false,
  error: null,

  // Actions
  purchaseTokens: async () => ({checkoutUrl: "", sessionId: ""}),
  getTokenBalance: async () => {
    // Default implementation
  },
  getTokenPricing: async () => {
    // Default implementation
  },
  getPaymentHistory: async () => {
    // Default implementation
  },
  retryFailedOperation: async () => {
    // Default implementation
  },
  clearError: () => {
    // Default implementation
  },
  refreshData: async () => {
    // Default implementation
  }
});

export const PaymentProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {user} = useUser();
  const [tokenBalance, setTokenBalance] = useState<TokenBalance | null>(null);
  const [tokenPricing, setTokenPricing] = useState<TokenPricing | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<PaymentError | null>(null);
  const [failedOperations, setFailedOperations] = useState<Map<string, () => Promise<void>>>(new Map());
  const initializedRef = useRef(false);
  const lastFetchTimeRef = useRef<number>(0);

  /**
   * Get current token balance
   * @param force - Force refresh even if recently fetched
   */
  const getTokenBalance = useCallback(
    async (force: boolean = false): Promise<void> => {
      if (!user.authenticated) {
        setTokenBalance(DEFAULT_TOKEN_BALANCE);
        return;
      }

      // Prevent excessive API calls - only fetch if more than 5 seconds since last fetch or forced
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTimeRef.current;
      if (!force && timeSinceLastFetch < 5000) {
        return;
      }

      lastFetchTimeRef.current = now;
      setIsLoading(true);
      setError(null);

      try {
        const balance = await paymentService.getTokenBalance(user.jwtToken);
        setTokenBalance(balance);
      } catch (err) {
        const paymentError = err as PaymentError;
        setError(paymentError);

        if (paymentError.retryable) {
          const operationId = `getTokenBalance-${Date.now()}`;
          setFailedOperations((prev) => new Map(prev.set(operationId, () => getTokenBalance(force))));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [user.authenticated, user.jwtToken]
  );

  /**
   * Get token pricing information (public - works with or without auth)
   */
  const getTokenPricing = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Pass jwtToken if authenticated, undefined otherwise (public access)
      const pricing = await paymentService.getTokenPricing(user.authenticated ? user.jwtToken : undefined);
      setTokenPricing(pricing);
    } catch (err) {
      const paymentError = err as PaymentError;
      setError(paymentError);

      if (paymentError.retryable) {
        const operationId = `getTokenPricing-${Date.now()}`;
        setFailedOperations((prev) => new Map(prev.set(operationId, getTokenPricing)));
      }
    } finally {
      setIsLoading(false);
    }
  }, [user.authenticated, user.jwtToken]);

  /**
   * Get payment history with pagination
   */
  const getPaymentHistory = useCallback(
    async (pagination: PaginationParams = DEFAULT_PAGINATION): Promise<void> => {
      if (!user.authenticated) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const history = await paymentService.getPaymentHistory(user.jwtToken, pagination);

        // If this is a paginated request (offset > 0), append to existing history
        if (pagination.offset > 0 && paymentHistory) {
          setPaymentHistory({
            ...history,
            transactions: [...paymentHistory.transactions, ...history.transactions]
          });
        } else {
          setPaymentHistory(history);
        }
      } catch (err) {
        const paymentError = err as PaymentError;
        setError(paymentError);

        if (paymentError.retryable) {
          const operationId = `getPaymentHistory-${Date.now()}`;
          setFailedOperations((prev) => new Map(prev.set(operationId, () => getPaymentHistory(pagination))));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [user.authenticated, paymentHistory]
  );

  /**
   * Purchase tokens through Stripe Checkout
   * Note: This will redirect to Stripe, so loading state and cleanup won't complete
   */
  const purchaseTokens = useCallback(
    async (request: PurchaseRequest): Promise<PurchaseResult> => {
      if (!user.authenticated) {
        throw new Error("User not authenticated");
      }

      // Don't set global loading state - redirect happens immediately
      // The component handles its own loading state
      setError(null);

      try {
        const result = await paymentService.purchaseTokens(user.jwtToken, request);
        // If we reach this point, redirect happened
        return result;
      } catch (err) {
        const paymentError = err as PaymentError;
        setError(paymentError);
        throw paymentError;
      }
    },
    [user.authenticated, user.jwtToken]
  );

  /**
   * Retry a failed operation
   */
  const retryFailedOperation = useCallback(
    async (operationId: string): Promise<void> => {
      const operation = failedOperations.get(operationId);
      if (operation) {
        setFailedOperations((prev) => {
          const newMap = new Map(prev);
          newMap.delete(operationId);
          return newMap;
        });
        await operation();
      }
    },
    [failedOperations]
  );

  /**
   * Clear current error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Refresh all payment data
   */
  const refreshData = useCallback(async (): Promise<void> => {
    if (!user.authenticated) {
      return;
    }

    await Promise.allSettled([getTokenBalance(), getTokenPricing(), getPaymentHistory()]);
  }, [user.authenticated]); // Removed function dependencies to avoid infinite loops

  // Initialize payment data when user authenticates
  useEffect(() => {
    if (user.authenticated && user.jwtToken && !initializedRef.current) {
      // Mark as initialized to prevent re-running
      initializedRef.current = true;

      // Load initial data
      const loadInitialData = async () => {
        try {
          await Promise.allSettled([
            paymentService
              .getTokenBalance(user.jwtToken)
              .then(setTokenBalance)
              .catch(() => setTokenBalance(DEFAULT_TOKEN_BALANCE)),
            paymentService
              .getTokenPricing(user.jwtToken)
              .then(setTokenPricing)
              .catch(() => {
                // default implementation
              }),
            paymentService
              .getPaymentHistory(user.jwtToken, DEFAULT_PAGINATION)
              .then(setPaymentHistory)
              .catch(() => {
                // default implementation
              })
          ]);
        } catch {
          //console.warn("Failed to load initial payment data:", error);
        }
      };

      loadInitialData();
    } else if (!user.authenticated) {
      // Reset initialization flag and clear data when user logs out
      initializedRef.current = false;
      setTokenBalance(null);
      setTokenPricing(null);
      setPaymentHistory(null);
      setError(null);
    }
  }, [user.authenticated, user.jwtToken]); // Only depend on auth state

  // Refresh token balance when window regains focus (e.g., returning from Stripe)
  useEffect(() => {
    if (!user.authenticated) {
      return;
    }

    const handleWindowFocus = () => {
      // Force refresh when window regains focus
      getTokenBalance(true);
    };

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [user.authenticated, getTokenBalance]);

  const contextValue: PaymentContextInterface = {
    // State
    tokenBalance,
    tokenPricing,
    paymentHistory,
    isLoading,
    error,

    // Actions
    purchaseTokens,
    getTokenBalance,
    getTokenPricing,
    getPaymentHistory,
    retryFailedOperation,
    clearError,
    refreshData
  };

  return <PaymentContext.Provider value={contextValue}>{children}</PaymentContext.Provider>;
};

// Custom hook to use payment context
export const usePayment = (): PaymentContextInterface => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};
